import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Paper, Button, Chip, Alert, CircularProgress, TextField, MenuItem, LinearProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  FormControlLabel, Switch,
} from '@mui/material';

import { customerDocsAPI } from '../services/api';
import { usePermissions } from '../context/PermissionsContext';

const MAX_BYTES = 25 * 1024 * 1024;
const ALLOWED = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'md', 'csv', 'png', 'jpg', 'jpeg'];
const extOf = (name) => (name.includes('.') ? name.split('.').pop().toLowerCase() : '');

const errText = (err, fallback) => err.response?.data?.detail || fallback;
const fmtTime = (d) => (d ? new Date(d).toLocaleString() : '-');
const fmtSize = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

// Errors from a blob download arrive as a Blob; read the message out of it
function readBlobText(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('unreadable'));
    reader.readAsText(blob);
  });
}

async function blobError(err, fallback) {
  const data = err.response?.data;
  if (data instanceof Blob) {
    try { return JSON.parse(await readBlobText(data)).detail || fallback; } catch (e) { return fallback; }
  }
  return errText(err, fallback);
}

function saveBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

// ── Document types ─────────────────────────────────────────────────────────

function ManageTypes({ onClose }) {
  const [rows, setRows] = useState([]);
  const [newName, setNewName] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setRows((await customerDocsAPI.types()).data.map((t) => ({ ...t, draft: t.name }))); }
    catch (err) { setError(errText(err, 'Could not load the types')); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (fn) => {
    setBusy(true); setError(null);
    try { await fn(); await load(); } catch (err) { setError(errText(err, 'That did not work')); } finally { setBusy(false); }
  };

  return (
    <Dialog open onClose={() => !busy && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>Document types</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        <Box sx={{ display: 'flex', gap: 1, mb: 2, pt: 1 }}>
          <TextField size="small" fullWidth label="New type" value={newName} onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && newName.trim() && !busy) run(() => customerDocsAPI.createType({ name: newName.trim(), sort_order: Math.max(0, ...rows.map((r) => r.sort_order)) + 10 }).then(() => setNewName(''))); }} />
          <Button variant="contained" disabled={!newName.trim() || busy}
            onClick={() => run(() => customerDocsAPI.createType({ name: newName.trim(), sort_order: Math.max(0, ...rows.map((r) => r.sort_order)) + 10 }).then(() => setNewName('')))}>Add</Button>
        </Box>
        <Table size="small">
          <TableHead><TableRow><TableCell>Name</TableCell><TableCell align="right">Documents</TableCell><TableCell align="center">Shown</TableCell></TableRow></TableHead>
          <TableBody>
            {rows.map((t) => (
              <TableRow key={t.id}>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField size="small" value={t.draft} disabled={busy} sx={{ minWidth: 180 }}
                      onChange={(e) => setRows((all) => all.map((r) => (r.id === t.id ? { ...r, draft: e.target.value } : r)))} />
                    {t.draft.trim() && t.draft.trim() !== t.name && <Button size="small" disabled={busy} onClick={() => run(() => customerDocsAPI.updateType(t.id, { name: t.draft.trim() }))}>Save</Button>}
                  </Box>
                </TableCell>
                <TableCell align="right">{t.documents}</TableCell>
                <TableCell align="center">
                  <Button size="small" disabled={busy} color={t.is_active ? 'primary' : 'warning'}
                    onClick={() => run(() => customerDocsAPI.updateType(t.id, { is_active: !t.is_active }))}>{t.is_active ? 'Shown' : 'Hidden'}</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          Hiding a type removes it from the choices for new documents; existing documents keep it. Types cannot be deleted.
        </Typography>
      </DialogContent>
      <DialogActions><Button onClick={onClose} disabled={busy}>Done</Button></DialogActions>
    </Dialog>
  );
}

// ── Upload ─────────────────────────────────────────────────────────────────

function UploadDialog({ customer, types, onClose }) {
  const [files, setFiles] = useState([]);
  const [docType, setDocType] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  const inputRef = useRef(null);

  const patch = (key, changes) => setFiles((all) => all.map((f) => (f.key === key ? { ...f, ...changes } : f)));

  const addFiles = (list) => {
    const added = Array.from(list || []).map((file) => {
      const item = { key: `${file.name}-${file.size}-${Math.random()}`, file, title: file.name.replace(/\.[^.]+$/, '').replace(/[_]+/g, ' '), error: null, state: 'ready' };
      if (!ALLOWED.includes(extOf(file.name))) item.error = `This type of file cannot be uploaded. Allowed: ${ALLOWED.map((e) => `.${e}`).join(', ')}.`;
      else if (file.size > MAX_BYTES) item.error = `Too large (${fmtSize(file.size)}). The limit is 25 MB.`;
      else if (file.size === 0) item.error = 'The file is empty.';
      return item;
    });
    setFiles((all) => [...all, ...added]);
  };

  const waiting = files.filter((f) => !f.error && f.state !== 'done');
  const canUpload = !busy && waiting.length > 0 && !!docType;
  const doneCount = files.filter((f) => f.state === 'done').length;

  const upload = async () => {
    setBusy(true);
    for (const f of files) {
      if (f.error || f.state === 'done') continue;
      patch(f.key, { state: 'uploading', message: null });
      try {
        await customerDocsAPI.upload(customer.id, f.file, { doc_type: docType, title: f.title, description });
        patch(f.key, { state: 'done' });
      } catch (err) {
        patch(f.key, { state: 'failed', message: errText(err, 'Upload failed') });
      }
    }
    setBusy(false);
  };

  return (
    <Dialog open onClose={() => !busy && onClose(doneCount > 0)} maxWidth="md" fullWidth>
      <DialogTitle>Upload documents for {customer.cust_code} ({customer.cust_name})</DialogTitle>
      <DialogContent>
        <Paper variant="outlined"
          onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
          onDrop={(e) => { e.preventDefault(); setOver(false); addFiles(e.dataTransfer.files); }}
          sx={{ p: 3, textAlign: 'center', borderStyle: 'dashed', borderWidth: 2, borderColor: over ? 'primary.main' : 'divider', bgcolor: over ? 'action.hover' : 'transparent', mb: 2, mt: 1 }}>
          <Typography gutterBottom>Drop files here</Typography>
          <input ref={inputRef} type="file" multiple hidden onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
          <Button variant="outlined" onClick={() => inputRef.current.click()} disabled={busy}>Choose files</Button>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            PDF, Word, Excel, PowerPoint, text, CSV and PNG/JPG images, up to 25 MB each.
          </Typography>
        </Paper>

        {files.map((f) => (
          <Box key={f.key} sx={{ display: 'flex', gap: 1.5, alignItems: 'center', py: 1, borderBottom: '1px solid', borderColor: 'divider', flexWrap: 'wrap' }}>
            <Box sx={{ flex: '0 0 200px', minWidth: 140 }}>
              <Typography variant="body2" noWrap title={f.file.name}>{f.file.name}</Typography>
              <Typography variant="caption" color="text.secondary">{fmtSize(f.file.size)}</Typography>
            </Box>
            {f.error ? <Alert severity="error" sx={{ py: 0, flex: 1 }}>{f.error}</Alert> : (
              <TextField size="small" label="Title" value={f.title} sx={{ flex: 1, minWidth: 220 }} disabled={f.state === 'uploading' || f.state === 'done'}
                onChange={(e) => patch(f.key, { title: e.target.value })} />
            )}
            {f.state === 'uploading' && <CircularProgress size={20} />}
            {f.state === 'done' && <Chip size="small" color="success" label="Uploaded" />}
            {f.state === 'failed' && <Chip size="small" color="error" label={f.message} sx={{ maxWidth: 320 }} />}
            {(f.state === 'ready' || f.state === 'failed') && !busy && (
              <Button size="small" onClick={() => setFiles((all) => all.filter((x) => x.key !== f.key))}>Remove</Button>
            )}
          </Box>
        ))}
        {busy && <LinearProgress sx={{ mt: 1 }} />}

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 3 }}>
          <TextField select size="small" label="Document type (required)" value={docType} onChange={(e) => setDocType(e.target.value)} sx={{ minWidth: 220 }} disabled={busy}>
            {types.filter((t) => t.is_active).map((t) => <MenuItem key={t.id} value={String(t.id)}>{t.name}</MenuItem>)}
          </TextField>
          <TextField size="small" label="Description (optional, applies to all)" value={description} onChange={(e) => setDescription(e.target.value)} sx={{ flex: 1, minWidth: 260 }} disabled={busy} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(doneCount > 0)} disabled={busy}>{doneCount > 0 && waiting.length === 0 ? 'Done' : 'Cancel'}</Button>
        <Button variant="contained" disabled={!canUpload} onClick={upload}>{busy ? 'Uploading…' : `Upload ${waiting.length || ''} file${waiting.length === 1 ? '' : 's'}`.replace('  ', ' ')}</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── New version ────────────────────────────────────────────────────────────

function NewVersionDialog({ doc, onClose }) {
  const [file, setFile] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  const problem = !file ? null
    : !ALLOWED.includes(extOf(file.name)) ? 'This type of file cannot be uploaded.'
    : file.size > MAX_BYTES ? 'The file is larger than 25 MB.' : null;

  const send = async () => {
    setBusy(true); setError(null);
    try { await customerDocsAPI.newVersion(doc.id, file, note); onClose(true); }
    catch (err) { setError(errText(err, 'Upload failed')); setBusy(false); }
  };

  return (
    <Dialog open onClose={() => !busy && onClose(false)} maxWidth="sm" fullWidth>
      <DialogTitle>New version of “{doc.title}”</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The current file (version {doc.current_version}, {doc.original_filename}) stays in the history and can still be downloaded.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <input ref={inputRef} type="file" hidden onChange={(e) => { setFile(e.target.files?.[0] || null); e.target.value = ''; }} />
        <Button variant="outlined" onClick={() => inputRef.current.click()} disabled={busy}>{file ? 'Choose a different file' : 'Choose the new file'}</Button>
        {file && <Typography variant="body2" sx={{ mt: 1 }}>{file.name} ({fmtSize(file.size)})</Typography>}
        {problem && <Alert severity="error" sx={{ mt: 1 }}>{problem}</Alert>}
        <TextField fullWidth size="small" label="What changed? (optional)" value={note} onChange={(e) => setNote(e.target.value)} sx={{ mt: 2 }} disabled={busy} />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)} disabled={busy}>Cancel</Button>
        <Button variant="contained" disabled={!file || !!problem || busy} onClick={send}>{busy ? 'Uploading…' : 'Upload new version'}</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── History ────────────────────────────────────────────────────────────────

function HistoryDialog({ doc, onClose, onDownload }) {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => { customerDocsAPI.detail(doc.id).then((r) => setDetail(r.data)).catch((e) => setError(errText(e, 'Could not load the history'))); }, [doc.id]);

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>History: {doc.title}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error">{error}</Alert>}
        {!detail && !error && <CircularProgress size={24} />}
        {detail && (
          <>
            <Typography variant="subtitle2" gutterBottom>Versions</Typography>
            <Table size="small">
              <TableHead><TableRow><TableCell>Version</TableCell><TableCell>File</TableCell><TableCell>Size</TableCell><TableCell>Uploaded</TableCell><TableCell>Note</TableCell><TableCell /></TableRow></TableHead>
              <TableBody>
                {detail.versions.map((v) => (
                  <TableRow key={v.version}>
                    <TableCell>{v.version}{v.version === detail.current_version ? ' (current)' : ''}</TableCell>
                    <TableCell>{v.original_filename}</TableCell>
                    <TableCell>{fmtSize(v.size_bytes)}</TableCell>
                    <TableCell>{fmtTime(v.uploaded_at)}<br /><Typography variant="caption" color="text.secondary">{v.uploaded_by}</Typography></TableCell>
                    <TableCell>{v.change_note}</TableCell>
                    <TableCell align="right"><Button size="small" onClick={() => onDownload(detail, v)}>Download</Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Typography variant="subtitle2" sx={{ mt: 3 }} gutterBottom>Who did what</Typography>
            <Table size="small">
              <TableBody>
                {detail.activity.map((a, i) => (
                  <TableRow key={i}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtTime(a.at)}</TableCell>
                    <TableCell>{a.actor}</TableCell>
                    <TableCell>{a.action.replace('_', ' ')}{a.detail?.version ? ` (version ${a.detail.version})` : ''}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}

// ── The section on the customer page ───────────────────────────────────────

export default function CustomerDocuments({ customer }) {
  const { isAdmin } = usePermissions();
  const [docs, setDocs] = useState([]);
  const [types, setTypes] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [dialog, setDialog] = useState(null);   // {type: 'upload' | 'types' | 'version' | 'history', doc}

  const load = useCallback(async () => {
    try {
      const [d, t] = await Promise.all([customerDocsAPI.list(customer.id, showArchived), customerDocsAPI.types()]);
      setDocs(d.data.documents); setTypes(t.data); setError(null);
    } catch (err) {
      setError(errText(err, 'Could not load the documents'));
    } finally {
      setLoading(false);
    }
  }, [customer.id, showArchived]);
  useEffect(() => { load(); }, [load]);

  const download = async (doc, version) => {
    try {
      const res = await customerDocsAPI.download(doc.id, version?.version);
      saveBlob(res.data, version?.original_filename || doc.original_filename);
    } catch (err) {
      setError(await blobError(err, 'Could not download the file'));
    }
  };

  const setStatus = async (doc, status) => {
    try { await customerDocsAPI.update(doc.id, { status }); setSuccess(status === 'archived' ? `“${doc.title}” archived` : `“${doc.title}” restored`); load(); }
    catch (err) { setError(errText(err, 'That did not work')); }
  };

  const closeDialog = (changed) => { setDialog(null); if (changed) { setSuccess('Saved'); load(); } };

  return (
    <Paper sx={{ p: 3, mt: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h6">Documents</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControlLabel control={<Switch size="small" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />} label="Show archived" />
          {isAdmin() && <Button size="small" onClick={() => setDialog({ type: 'types' })}>Document types</Button>}
          <Button variant="contained" onClick={() => setDialog({ type: 'upload' })}>Upload documents</Button>
        </Box>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {loading ? <CircularProgress size={24} /> : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow><TableCell>Document</TableCell><TableCell>Type</TableCell><TableCell>Version</TableCell><TableCell>Size</TableCell><TableCell>Updated</TableCell><TableCell align="right">Actions</TableCell></TableRow>
            </TableHead>
            <TableBody>
              {docs.map((d) => (
                <TableRow key={d.id} hover sx={d.status === 'archived' ? { opacity: 0.6 } : undefined}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{d.title}{d.status === 'archived' ? ' (archived)' : ''}</Typography>
                    <Typography variant="caption" color="text.secondary">{d.original_filename}{d.description ? ` · ${d.description}` : ''}</Typography>
                  </TableCell>
                  <TableCell><Chip size="small" variant="outlined" label={d.type_name} /></TableCell>
                  <TableCell>{d.current_version}</TableCell>
                  <TableCell>{fmtSize(d.size_bytes)}</TableCell>
                  <TableCell>{fmtTime(d.updated_at)}<br /><Typography variant="caption" color="text.secondary">{d.updated_by}</Typography></TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Button size="small" onClick={() => download(d)}>Download</Button>
                    {d.status === 'active' && <Button size="small" onClick={() => setDialog({ type: 'version', doc: d })}>New version</Button>}
                    <Button size="small" onClick={() => setDialog({ type: 'history', doc: d })}>History</Button>
                    {isAdmin() && (d.status === 'active'
                      ? <Button size="small" color="warning" onClick={() => setStatus(d, 'archived')}>Archive</Button>
                      : <Button size="small" onClick={() => setStatus(d, 'active')}>Restore</Button>)}
                  </TableCell>
                </TableRow>
              ))}
              {docs.length === 0 && <TableRow><TableCell colSpan={6} align="center">No documents yet for {customer.cust_code}</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {dialog?.type === 'upload' && <UploadDialog customer={customer} types={types} onClose={closeDialog} />}
      {dialog?.type === 'version' && <NewVersionDialog doc={dialog.doc} onClose={closeDialog} />}
      {dialog?.type === 'history' && <HistoryDialog doc={dialog.doc} onClose={() => setDialog(null)} onDownload={(detail, v) => download({ id: detail.id, original_filename: v.original_filename }, v)} />}
      {dialog?.type === 'types' && <ManageTypes onClose={() => { setDialog(null); load(); }} />}
    </Paper>
  );
}
